import { useApprovalsPromo } from '@easy-expense/data-firestore-client';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ApprovalsTutorialModal } from './ApprovalsTutorialModal.component';
import { MagicShiny } from '../Shared/MagicShiny.component';

/**
 * "New Feature: Expense Approvals" banner, in the mobile banner's glowing card. A play icon and a
 * solid "Watch video" button lead, with a small ×, so the video reads as the main action. Clicking
 * anywhere but the × opens the approvals tutorial modal. Who sees it, and what dismissing and
 * enabling do, lives in `useApprovalsPromo` so web and mobile stay in step. The modal stays mounted
 * while open even after the card hides, since enabling approvals hides the card before the modal
 * shows its done step.
 */
export const ApprovalsPromoBanner: React.FC = () => {
  const [showModal, setShowModal] = React.useState(false);
  const navigate = useNavigate();
  const promo = useApprovalsPromo('web');

  if (!promo.isVisible && !showModal) {
    return null;
  }

  function onClick() {
    promo.trackBannerPressed();
    setShowModal(true);
  }

  function onDismiss(event: React.MouseEvent) {
    event.stopPropagation();
    promo.dismissBanner();
  }

  function onDone() {
    setShowModal(false);
  }

  function onViewSettings() {
    setShowModal(false);
    navigate('/settings?tab=workspace');
  }

  function onSkip() {
    promo.trackSkipped();
    setShowModal(false);
  }

  return (
    <Layout.Column>
      {promo.isVisible && <Spacer.Vertical size={12} />}
      {promo.isVisible && (
        <MagicShiny onClick={onClick}>
          <Layout.Row align px={16} py={12}>
            <Icon name="play-circle" size={36} color={theme.colors.brandPrimary} />
            <Spacer.Horizontal size={12} />

            <Layout.Column grow>
              <OpenSans.Primary weight="bold-700" size="s-16">
                {getTranslation('New Feature: Expense Approvals')}
              </OpenSans.Primary>
              <OpenSans.Secondary size="xs-12">
                {getTranslation("Watch how to approve your team's expenses")}
              </OpenSans.Secondary>
            </Layout.Column>

            <Spacer.Horizontal size={16} />

            <Layout.Row align bg="brandPrimary" radius={100} px={16} py={8}>
              <Icon name="play" size={16} color={theme.colors.buttonWhite} />
              <Spacer.Horizontal size={6} />
              <OpenSans.Primary color="buttonWhite" weight="bold-700" size="s-16">
                {getTranslation('Watch video')}
              </OpenSans.Primary>
            </Layout.Row>

            <Spacer.Horizontal size={24} />

            <Layout.PressableColumn
              onClick={onDismiss}
              aria-label={getTranslation('Dismiss')}
              style={{ backgroundColor: 'transparent', padding: 4 }}
            >
              <Icon name="close" size={16} color={theme.colors.gray} />
            </Layout.PressableColumn>
          </Layout.Row>
        </MagicShiny>
      )}
      <ApprovalsTutorialModal
        showModal={showModal}
        setShowModal={setShowModal}
        isAdmin={promo.isAdmin}
        onEnable={promo.enableApprovals}
        onSkip={onSkip}
        onDone={onDone}
        onViewSettings={onViewSettings}
      />
    </Layout.Column>
  );
};
